import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Search, Users, TrendingUp, Monitor, Mail, Video, Globe, BarChart3,
  Target, Layers, Phone, CheckCircle, ArrowRight, Star, Play,
  BookOpen, Award, Clock, DollarSign, Sparkles, FileText, MessageSquare,
  Zap, Eye, Calendar, GraduationCap, Rocket
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Training topics
const trainingTopics = [
  { 
    icon: Search, 
    title: "Google Ads", 
    desc: "Master search campaigns, call-only ads, Performance Max, and bid strategies for maximum ROI",
    color: "blue"
  },
  { 
    icon: Users, 
    title: "Meta Ads", 
    desc: "Create high-converting Facebook & Instagram ad campaigns targeting homeowners and service seekers",
    color: "purple"
  },
  { 
    icon: Globe, 
    title: "Facebook Marketplace", 
    desc: "Leverage marketplace listings and lead gen forms for local service calls",
    color: "cyan"
  },
  { 
    icon: TrendingUp, 
    title: "SEO", 
    desc: "Build organic traffic with optimized landing pages and local SEO strategies",
    color: "green"
  },
  { 
    icon: Search, 
    title: "Bing Ads", 
    desc: "Tap into Microsoft's search network for additional high-quality call volume",
    color: "blue"
  },
  { 
    icon: Monitor, 
    title: "Native Ads", 
    desc: "Run profitable campaigns on Taboola, Outbrain, and other native platforms",
    color: "orange"
  },
  { 
    icon: Layers, 
    title: "Display Advertising", 
    desc: "Retargeting and display strategies that drive phone calls",
    color: "pink"
  },
  { 
    icon: Target, 
    title: "Affiliate Marketing", 
    desc: "Build and scale affiliate partnerships for consistent call flow",
    color: "purple"
  },
  { 
    icon: Phone, 
    title: "Call Tracking (Ringba)", 
    desc: "Set up and optimize call tracking, routing, and analytics",
    color: "cyan"
  },
  { 
    icon: BarChart3, 
    title: "Landing Page Optimization", 
    desc: "Design high-converting landing pages that turn visitors into callers",
    color: "green"
  }
];

// Course plans
// Course 1 = Standard ($500, 8 weeks) — designed for FREELANCERS
// Course 2 = Elite ($1,000, 12 weeks) — designed for BUSINESS BUILDERS
const coursePlans = [
  {
    name: "Digital Growth Mastery",
    tier: "Standard",
    price: "$500",
    duration: "8 Weeks",
    sessions: "16 Live Sessions",
    goal: "Become a Freelancer",
    designedFor: "Aspiring freelancers ready to earn online",
    description:
      "Learn practical digital marketing and lead-generation skills to start earning independently as a freelancer.",
    level: "Beginner → Intermediate",
    modules: [
      { group: "Core Paid Ads",      items: ["Google Ads Fundamentals", "PPC Campaign Management", "Search & Display Ads"] },
      { group: "Social & Messaging", items: ["Meta Ads", "WhatsApp Marketing", "Email Marketing"] },
      { group: "Conversion Stack",   items: ["Lead Generation", "Landing Pages", "Website Development Basics"] },
      { group: "Data & AI",          items: ["Tracking & Analytics", "AI Tools for Marketing"] },
    ],
    included: [
      { icon: "Video",       label: "Interactive live classes" },
      { icon: "FileText",    label: "Hands-on assignments after every module" },
      { icon: "Layers",      label: "Plug-and-play campaign templates" },
      { icon: "MessageSquare", label: "Direct email support from instructors" },
      { icon: "Award",       label: "Completion certificate" },
    ],
    outcomes: [
      "Run campaigns for paying clients on day one",
      "Generate leads in multiple verticals",
      "Build conversion-ready websites and landing pages",
      "Work as a Freelancer on Upwork, Fiverr or direct clients",
      "Offer end-to-end digital marketing services",
      "Start earning a real online income",
    ],
    cta: "Start Your Freelance Journey",
    popular: true,
    badge: "Most Popular",
    accent: "purple",
  },
  {
    name: "Digital Growth Elite",
    tier: "Premium",
    price: "$1,000",
    duration: "12 Weeks",
    sessions: "24 Live Sessions",
    goal: "Build Your Business",
    designedFor: "Founders ready to scale their own agency",
    description:
      "Build advanced skills to launch, operate and scale your own digital-marketing and lead-generation business.",
    level: "Intermediate → Advanced",
    modules: [
      { group: "Everything in Standard", items: ["All 11 Standard modules included"] },
      { group: "Advanced Paid Media",    items: ["Advanced Google Ads", "Pay Per Call Campaigns", "Call Tracking", "Advanced Meta Ads"] },
      { group: "Automation & Systems",   items: ["WhatsApp Automation", "Email Automation", "AI Automation", "CRM Systems"] },
      { group: "Tech & Affiliates",      items: ["Website Development Advanced", "Software Development Fundamentals", "Affiliate Marketing"] },
      { group: "Business Operations",    items: ["Client Acquisition", "Business Scaling"] },
    ],
    included: [
      { icon: "Video",       label: "Interactive live classes" },
      { icon: "Zap",         label: "Priority 1-on-1 support" },
      { icon: "Eye",         label: "Personal campaign reviews by instructors" },
      { icon: "Layers",      label: "Premium templates + automation playbooks" },
      { icon: "Award",       label: "Completion certificate" },
    ],
    outcomes: [
      "Launch your own digital-marketing business",
      "Build repeatable lead-generation systems",
      "Generate inbound calls professionally at scale",
      "Acquire, onboard and manage clients",
      "Build and lead a small remote team",
      "Scale operations across multiple verticals",
      "Create long-term, predictable income streams",
    ],
    cta: "Build Your Business",
    popular: false,
    badge: "Premium",
    accent: "cyan",
  },
];

// Icon registry — lets the course-plan data stay as plain strings while still
// rendering real Lucide icons in the "Included" list.
const ICONS = {
  Video, FileText, Layers, MessageSquare, Award, Zap, Eye,
};
const Icon = ({ name, ...rest }) => {
  const C = ICONS[name] || CheckCircle;
  return <C {...rest} />;
};

const LearnPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-['Inter']" data-testid="learn-page">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-yellow-500/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-yellow-500/30">
              <BookOpen className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">PPC Courses</span>
            </motion.div>
            
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Master the Art of <span className="gradient-text">Generating Inbound Calls</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-gray-300 text-lg max-w-3xl mx-auto mb-10">
              Learn how to generate high-quality inbound phone calls using proven strategies across Google Ads, Meta Ads, SEO, and more. Turn your traffic into revenue.
            </motion.p>
            
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <a 
                href="#pricing" 
                className="gradient-btn text-white font-bold px-8 py-4 rounded-full inline-flex items-center gap-2"
              >
                View Course Plans <ArrowRight className="w-5 h-5" />
              </a>
              <a 
                href="#topics" 
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 border border-white/20 transition-all"
              >
                <Play className="w-5 h-5" /> Explore Topics
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section id="topics" className="py-20 bg-slate-50" data-testid="topics-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Training Content
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What You'll <span className="gradient-text">Learn</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive training covering all major traffic sources and strategies for generating inbound calls
            </motion.p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {trainingTopics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="service-card bg-white rounded-2xl p-6 border border-gray-100 hover:border-blue-200 text-center"
                data-testid={`topic-${topic.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 mx-auto ${
                  topic.color === 'blue' ? 'bg-blue-100' :
                  topic.color === 'purple' ? 'bg-purple-100' :
                  topic.color === 'cyan' ? 'bg-cyan-100' :
                  topic.color === 'green' ? 'bg-green-100' :
                  topic.color === 'orange' ? 'bg-orange-100' : 'bg-pink-100'
                }`}>
                  <topic.icon className={`w-7 h-7 ${
                    topic.color === 'blue' ? 'text-blue-600' :
                    topic.color === 'purple' ? 'text-purple-600' :
                    topic.color === 'cyan' ? 'text-cyan-600' :
                    topic.color === 'green' ? 'text-green-600' :
                    topic.color === 'orange' ? 'text-orange-600' : 'text-pink-600'
                  }`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{topic.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{topic.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section id="pricing" className="py-20 bg-gray-50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Course Pricing
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Choose Your <span className="gradient-text">Learning Path</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-600 text-lg max-w-2xl mx-auto">
              Select the plan that matches your goals and experience level
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-5 flex flex-wrap justify-center gap-2 text-xs font-semibold"
              data-testid="course-policy-strip"
            >
              <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                ✓ Live Classes Only
              </span>
              <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                ✓ Direct Instructor Support
              </span>
              <span className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                ✓ Real Assignments &amp; Templates
              </span>
              <span className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                ✓ Certificate on Completion
              </span>
            </motion.div>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {coursePlans.map((plan, index) => {
              const isPurple = plan.accent === "purple";
              const accentGradient = isPurple
                ? "from-purple-600 to-blue-600"
                : "from-cyan-600 to-blue-700";
              const accentBgGradient = isPurple
                ? "from-purple-50 via-blue-50 to-white"
                : "from-cyan-50 via-blue-50 to-white";
              const accentText = isPurple ? "text-purple-700" : "text-cyan-700";
              const accentBorder = isPurple ? "border-purple-500" : "border-cyan-500";
              const ringShadow = isPurple
                ? "shadow-purple-200/70"
                : "shadow-cyan-200/70";
              const moduleCount = plan.modules.reduce((n, g) => n + g.items.length, 0);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative bg-white rounded-3xl overflow-hidden flex flex-col border-2 ${accentBorder} shadow-2xl ${ringShadow} ${plan.popular ? 'md:scale-[1.02]' : ''}`}
                  data-testid={`plan-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {/* Top badge ribbon */}
                  <div className={`bg-gradient-to-r ${accentGradient} text-white px-6 py-2.5 flex items-center justify-between text-xs font-bold uppercase tracking-[0.14em]`}>
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      {plan.badge}
                    </span>
                    <span className="opacity-80">{plan.tier}</span>
                  </div>

                  {/* Header — title, price, designed-for */}
                  <div className={`p-7 bg-gradient-to-br ${accentBgGradient} border-b border-gray-100`}>
                    <div className="flex items-center gap-2 mb-2 text-[11px] font-bold uppercase tracking-[0.14em]">
                      <Rocket className={`w-3.5 h-3.5 ${accentText}`} />
                      <span className={accentText}>{plan.goal}</span>
                    </div>
                    <h3 className="text-2xl md:text-[28px] font-bold text-gray-900 leading-tight mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {plan.description}
                    </p>
                    <div className="flex items-end gap-3 mb-4">
                      <span className={`text-5xl md:text-6xl font-extrabold bg-gradient-to-r ${accentGradient} bg-clip-text text-transparent leading-none`}>
                        {plan.price}
                      </span>
                      <span className="text-gray-500 text-sm pb-1">
                        one-time · USD
                      </span>
                    </div>

                    {/* Stats strip */}
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <div className="rounded-lg bg-white/80 backdrop-blur-sm border border-gray-200 px-3 py-2 text-center">
                        <Calendar className={`w-4 h-4 mx-auto mb-1 ${accentText}`} />
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Duration</p>
                        <p className="text-sm font-bold text-gray-900">{plan.duration}</p>
                      </div>
                      <div className="rounded-lg bg-white/80 backdrop-blur-sm border border-gray-200 px-3 py-2 text-center">
                        <Video className={`w-4 h-4 mx-auto mb-1 ${accentText}`} />
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Sessions</p>
                        <p className="text-sm font-bold text-gray-900">{plan.sessions.replace(" Live Sessions","")}</p>
                      </div>
                      <div className="rounded-lg bg-white/80 backdrop-blur-sm border border-gray-200 px-3 py-2 text-center">
                        <Layers className={`w-4 h-4 mx-auto mb-1 ${accentText}`} />
                        <p className="text-[10px] uppercase tracking-wider text-gray-500">Modules</p>
                        <p className="text-sm font-bold text-gray-900">{moduleCount}</p>
                      </div>
                    </div>

                    <p className="text-xs text-gray-600 mt-4 italic">
                      <span className="font-semibold not-italic">Designed for:</span> {plan.designedFor} · <span className="font-semibold not-italic">Level:</span> {plan.level}
                    </p>
                  </div>

                  {/* Curriculum grouped */}
                  <div className="px-7 py-6 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-xs font-bold uppercase tracking-[0.14em] text-gray-500 flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" />
                        Curriculum
                      </h4>
                      <span className={`text-[11px] font-bold ${accentText}`}>
                        {moduleCount} modules
                      </span>
                    </div>
                    <div className="space-y-4">
                      {plan.modules.map((group, gi) => (
                        <div key={gi}>
                          <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${accentText}`}>
                            {group.group}
                          </p>
                          <ul className="space-y-1">
                            {group.items.map((item, ii) => (
                              <li key={ii} className="flex items-start gap-2 text-sm text-gray-700">
                                <CheckCircle className={`w-3.5 h-3.5 flex-shrink-0 mt-1 ${accentText}`} />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* What's included — icon list */}
                  <div className="px-7 py-6 bg-emerald-50/60 border-b border-gray-100">
                    <h4 className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-800 mb-3 flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5" />
                      What's included
                    </h4>
                    <ul className="space-y-2.5">
                      {plan.included.map((inc, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <div className="w-7 h-7 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                            <Icon name={inc.icon} className="w-3.5 h-3.5 text-emerald-700" strokeWidth={2.5} />
                          </div>
                          <span className="text-emerald-950 pt-1 leading-tight">{inc.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Learning outcomes */}
                  <div className="px-7 py-6 border-b border-gray-100">
                    <h4 className="text-xs font-bold uppercase tracking-[0.14em] text-gray-500 mb-3 flex items-center gap-1.5">
                      <GraduationCap className="w-3.5 h-3.5" />
                      By the end you can
                    </h4>
                    <ul className="space-y-2">
                      {plan.outcomes.map((outcome, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                          <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 mt-1 ${accentText}`} />
                          <span className="leading-relaxed">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="px-7 py-6 mt-auto">
                    <Link
                      to={`/course-apply?plan=${encodeURIComponent(plan.name)}`}
                      className={`group w-full py-4 rounded-xl font-bold text-base inline-flex items-center justify-center gap-2 text-white shadow-lg shadow-purple-900/20 bg-gradient-to-r ${accentGradient} hover:opacity-95 transition-all`}
                      data-testid={`plan-${plan.tier.toLowerCase()}-cta`}
                    >
                      {plan.cta}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <p className="text-center text-[11px] text-gray-500 mt-3">
                      Live classes only · Limited seats per cohort
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Learn With Us */}
      <section className="py-20 bg-slate-50" data-testid="why-learn-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-4">
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Learn From <span className="gradient-text">Industry Experts</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Our training is developed by professionals who have generated millions of dollars in call revenue. 
                Get real-world strategies that work, not just theory.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  "Proven strategies from active Pay Per Call professionals",
                  "Real campaign case studies with actual results",
                  "Step-by-step implementation guides",
                  "Direct access to instructors and community",
                  "Lifetime updates as platforms evolve"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <a 
                href="#pricing" 
                className="gradient-btn text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2"
              >
                Get Started Today <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 text-center">
                <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-1">100+</div>
                <p className="text-gray-600">Students Trained</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 text-center">
                <DollarSign className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-1">$1M+</div>
                <p className="text-gray-600">Revenue Generated</p>
              </div>
              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-6 text-center">
                <Phone className="w-12 h-12 text-cyan-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-1">50K+</div>
                <p className="text-gray-600">Calls Generated</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 text-center">
                <Star className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-gray-900 mb-1">4.8</div>
                <p className="text-gray-600">Student Rating</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden" data-testid="learn-cta">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
              Join our training program and learn how to generate consistent, high-quality inbound calls
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/course-apply" 
                className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 transition-all"
              >
                Apply Now <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/contact" 
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 border border-white/30 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LearnPage;
